import { AnimaliaUseCases } from '../../application/animaliaUseCasesFactory';
import {
  AnimaliaListingPageDTO,
  AnimaliaListingTaxonomyLandingDTO,
} from '../../application/mappers/AnimaliaListingMapper';
import { AnimaliaSearchPageDTO } from '../../application/mappers/AnimaliaSearchMapper';
import {
  buildAnimaliaTaxonomyCanonicalRoute,
  resolveAnimaliaListingFiltersFromLocation,
  toAnimaliaNavigableFilters,
} from '../../application/taxonomy/AnimaliaNavigableTaxonomy';
import { buildRoute, getCurrentLocation } from '../../../../shared/ui/router';
import {
  applySeoMetadata,
  buildSiteUrl,
  createWebsiteJsonLd,
} from '../../../../shared/ui/seo/seoMetadata';
import '../components/listing/hero/mv-animalia-listing-hero';
import '../components/listing/section-head/mv-animalia-listing-section-head';
import '../components/listing/grid/mv-animalia-listing-grid';
import '../components/listing/status/mv-animalia-listing-status';
import { MvAnimaliaListingHero } from '../components/listing/hero/mv-animalia-listing-hero';
import { MvAnimaliaListingGrid } from '../components/listing/grid/mv-animalia-listing-grid';
import {
  AnimaliaListingSectionHeadState,
  MvAnimaliaListingSectionHead,
} from '../components/listing/section-head/mv-animalia-listing-section-head';
import { MvAnimaliaListingStatus } from '../components/listing/status/mv-animalia-listing-status';

type ViewState = 'idle' | 'loading' | 'ready' | 'empty' | 'error';
type ExplorationMode = 'listing' | 'search';

const AUTO_SEARCH_THRESHOLDS = [5, 10] as const;

export class MvAnimaliaHomePage extends HTMLElement {
  private animaliaUseCases!: AnimaliaUseCases;

  private baseListingData: AnimaliaListingPageDTO | null = null;

  private searchData: AnimaliaSearchPageDTO | null = null;

  private explorationMode: ExplorationMode = 'listing';

  private viewState: ViewState = 'idle';

  private errorMessage = '';

  private paginationErrorMessage = '';

  private draftQuery = '';

  private appliedQuery = '';

  private pendingQuery = '';

  private searchRequestSequence = 0;

  private activeSearchRequestId = 0;

  private isTaxonomyEditorialExpanded = false;

  private readonly handleEditorialToggleClick = () => {
    this.isTaxonomyEditorialExpanded = !this.isTaxonomyEditorialExpanded;
    this.render();
    this.addEventListeners();
  };

  private readonly handleRetryRequested = () => {
    if (this.explorationMode === 'search' && this.getCurrentSearchQuery() !== null) {
      void this.runSearch(this.getCurrentSearchQuery() ?? '', 'manual');
      return;
    }

    void this.loadListing(true);
  };

  private readonly handleLoadMoreClick = () => {
    if (this.explorationMode === 'listing') {
      void this.loadListing(false);
    }
  };

  private readonly handleSearchQueryChanged = (event: Event) => {
    const customEvent = event as CustomEvent<{ value: string }>;
    const nextValue = customEvent.detail?.value ?? '';
    const previousValue = this.draftQuery;

    this.draftQuery = nextValue;

    if (nextValue.trim() === '') {
      this.clearSearch();
      return;
    }

    const crossedThreshold = this.getCrossedAutoThreshold(previousValue, nextValue);

    if (crossedThreshold !== null) {
      void this.runSearch(nextValue, 'auto');
    }
  };

  private readonly handleSearchSubmitted = (event: Event) => {
    const customEvent = event as CustomEvent<{ value: string }>;

    void this.runSearch(customEvent.detail?.value ?? this.draftQuery, 'manual');
  };

  private readonly handleSearchCleared = () => {
    this.clearSearch();
  };

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
  }

  setUseCases(animaliaUseCases: AnimaliaUseCases): void {
    this.animaliaUseCases = animaliaUseCases;
  }

  connectedCallback(): void {
    this.render();
    this.addEventListeners();

    if (this.viewState === 'idle') {
      void this.initializePage();
    }
  }

  disconnectedCallback(): void {
    this.removeEventListeners();
  }

  private async initializePage(): Promise<void> {
    const initialSearchQuery = this.getSearchQueryFromLocation();

    if (initialSearchQuery !== null) {
      this.draftQuery = initialSearchQuery;
      this.normalizeSearchLocation(initialSearchQuery);
      void this.loadListing(true, true);
      await this.runSearch(initialSearchQuery, 'url');
      return;
    }

    await this.loadListing(true);
  }

  private addEventListeners(): void {
    this.shadowRoot?.removeEventListener('retry-requested', this.handleRetryRequested as EventListener);
    this.shadowRoot?.removeEventListener('search-query-changed', this.handleSearchQueryChanged as EventListener);
    this.shadowRoot?.removeEventListener('search-submitted', this.handleSearchSubmitted as EventListener);
    this.shadowRoot?.removeEventListener('search-cleared', this.handleSearchCleared as EventListener);
    this.shadowRoot
      ?.querySelector('[data-editorial-toggle]')
      ?.removeEventListener('click', this.handleEditorialToggleClick);
    this.shadowRoot
      ?.querySelector('[data-load-more]')
      ?.removeEventListener('click', this.handleLoadMoreClick);

    this.shadowRoot?.addEventListener('retry-requested', this.handleRetryRequested as EventListener);
    this.shadowRoot?.addEventListener('search-query-changed', this.handleSearchQueryChanged as EventListener);
    this.shadowRoot?.addEventListener('search-submitted', this.handleSearchSubmitted as EventListener);
    this.shadowRoot?.addEventListener('search-cleared', this.handleSearchCleared as EventListener);
    this.shadowRoot?.querySelector('[data-editorial-toggle]')?.addEventListener('click', this.handleEditorialToggleClick);
    this.shadowRoot?.querySelector('[data-load-more]')?.addEventListener('click', this.handleLoadMoreClick);
  }

  private removeEventListeners(): void {
    this.shadowRoot?.removeEventListener('retry-requested', this.handleRetryRequested as EventListener);
    this.shadowRoot?.removeEventListener('search-query-changed', this.handleSearchQueryChanged as EventListener);
    this.shadowRoot?.removeEventListener('search-submitted', this.handleSearchSubmitted as EventListener);
    this.shadowRoot?.removeEventListener('search-cleared', this.handleSearchCleared as EventListener);
    this.shadowRoot
      ?.querySelector('[data-editorial-toggle]')
      ?.removeEventListener('click', this.handleEditorialToggleClick);
    this.shadowRoot
      ?.querySelector('[data-load-more]')
      ?.removeEventListener('click', this.handleLoadMoreClick);
  }

  private async loadListing(reset: boolean, silent = false): Promise<void> {
    if (!this.animaliaUseCases) {
      throw new Error('Animalia use cases have not been provided.');
    }

    const shouldRenderLoading = !silent && this.explorationMode === 'listing';

    if (shouldRenderLoading) {
      this.viewState = 'loading';

      if (reset) {
        this.errorMessage = '';
        this.paginationErrorMessage = '';
      }

      this.render();
      this.addEventListeners();
    }

    try {
      this.baseListingData = await this.animaliaUseCases.fetchAnimaliaListing.execute({
        filters: this.getActiveFiltersFromLocation(),
        reset,
      });

      if (this.explorationMode === 'listing') {
        this.viewState = this.baseListingData.items.length > 0 ? 'ready' : 'empty';
        this.paginationErrorMessage = '';
      }
    } catch (error: unknown) {
      if (silent) {
        console.error('Animalia listing failed to load in background.', error);
        return;
      }

      const message = error instanceof Error ? error.message : 'No se pudieron recuperar las fichas.';

      if (!reset && this.baseListingData) {
        this.viewState = 'ready';
        this.paginationErrorMessage = message;
      } else {
        this.viewState = 'error';
        this.errorMessage = message;
      }

      console.error('Animalia listing failed to load.', error);
    }

    if (!silent) {
      this.applySeo();
      this.render();
      this.addEventListeners();
    }
  }

  private async runSearch(rawQuery: string, trigger: 'auto' | 'manual' | 'url'): Promise<void> {
    if (!this.animaliaUseCases) {
      throw new Error('Animalia use cases have not been provided.');
    }

    const normalizedQuery = rawQuery.trim();

    if (normalizedQuery === '') {
      this.clearSearch();
      return;
    }

    this.draftQuery = rawQuery;
    this.pendingQuery = normalizedQuery;
    this.errorMessage = '';
    this.paginationErrorMessage = '';
    this.searchData = null;
    this.explorationMode = 'search';
    this.viewState = 'loading';

    if (trigger !== 'url') {
      this.normalizeSearchLocation(normalizedQuery);
    }

    const requestId = ++this.searchRequestSequence;
    this.activeSearchRequestId = requestId;

    this.render();
    this.addEventListeners();
    this.restoreSearchFocus(trigger);

    try {
      const result = await this.animaliaUseCases.searchAnimalia.execute({
        query: normalizedQuery,
        detailReturnTo: this.getCurrentListingRoute(),
      });

      if (requestId !== this.activeSearchRequestId) {
        return;
      }

      this.explorationMode = 'search';
      this.searchData = result;
      this.appliedQuery = result.query;
      this.pendingQuery = '';
      this.viewState = result.items.length > 0 ? 'ready' : 'empty';
    } catch (error: unknown) {
      if (requestId !== this.activeSearchRequestId) {
        return;
      }

      this.explorationMode = 'search';
      this.searchData = null;
      this.appliedQuery = normalizedQuery;
      this.pendingQuery = '';
      this.viewState = 'error';
      this.errorMessage = error instanceof Error ? error.message : 'No se pudo completar la busqueda.';

      console.error('Animalia search failed to load.', error);
    }

    this.render();
    this.addEventListeners();
    this.restoreSearchFocus(trigger);
    this.applySeo();
  }

  private clearSearch(): void {
    this.invalidateSearchRequests();
    this.explorationMode = 'listing';
    this.searchData = null;
    this.pendingQuery = '';
    this.appliedQuery = '';
    this.draftQuery = '';
    this.errorMessage = '';
    this.paginationErrorMessage = '';
    this.replaceLocation(this.getListingBaseHref(false));

    this.viewState = this.baseListingData?.items.length ? 'ready' : 'loading';
    this.applySeo();
    this.render();
    this.addEventListeners();

    void this.loadListing(true);
  }

  private invalidateSearchRequests(): void {
    this.activeSearchRequestId = ++this.searchRequestSequence;
  }

  private render(): void {
    if (!this.shadowRoot) {
      return;
    }

    const showLoadMore =
      this.explorationMode === 'listing' &&
      this.baseListingData !== null &&
      this.baseListingData.hasMore &&
      this.viewState !== 'error' &&
      this.viewState !== 'empty';
    const showActiveFilters =
      this.explorationMode === 'listing' && this.getVisibleActiveFilters().length > 0;

    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: block;
        }

        * {
          box-sizing: border-box;
        }

        .page-shell {
          display: grid;
          gap: 18px;
        }

        .initial-loading {
          display: grid;
          gap: 18px;
          padding: 12px 0 4px;
        }

        .initial-loading-block,
        .initial-loading-card {
          border-radius: 24px;
          background:
            linear-gradient(90deg, rgba(49, 70, 61, 0.06), rgba(49, 70, 61, 0.12), rgba(49, 70, 61, 0.06));
          background-size: 200% 100%;
          animation: placeholder-shimmer 1.4s ease-in-out infinite;
        }

        .initial-loading-block {
          min-height: 96px;
        }

        .initial-loading-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 18px;
        }

        .initial-loading-card {
          min-height: 320px;
        }

        @keyframes placeholder-shimmer {
          0% {
            background-position: 200% 0;
          }

          100% {
            background-position: -200% 0;
          }
        }

        .taxonomy-landing {
          display: grid;
          gap: 22px;
          padding: 24px;
          border-radius: 30px;
          background:
            linear-gradient(135deg, rgba(49, 70, 61, 0.98), rgba(95, 120, 128, 0.84)),
            linear-gradient(180deg, rgba(255, 255, 255, 0.04), rgba(255, 255, 255, 0));
          color: #f7f3ed;
          box-shadow: 0 18px 38px rgba(31, 42, 35, 0.1);
        }

        .taxonomy-landing-layout {
          display: grid;
          grid-template-columns: minmax(260px, 0.86fr) minmax(0, 1.14fr);
          gap: 24px;
          align-items: center;
        }

        .taxonomy-media {
          position: relative;
          min-height: 320px;
          border-radius: 24px;
          overflow: hidden;
          border: 1px solid rgba(255, 255, 255, 0.12);
          background: rgba(255, 255, 255, 0.06);
        }

        .taxonomy-media img,
        .taxonomy-media-fallback {
          display: block;
          width: 100%;
          height: 100%;
          aspect-ratio: 4 / 5;
          object-fit: cover;
        }

        .taxonomy-media-fallback {
          display: grid;
          place-items: center;
          background:
            radial-gradient(circle at top left, rgba(235, 229, 219, 0.34), transparent 30%),
            linear-gradient(135deg, rgba(95, 120, 128, 0.92), rgba(49, 70, 61, 0.98));
          color: rgba(247, 243, 237, 0.84);
          font-family: Georgia, 'Times New Roman', serif;
          font-size: clamp(2rem, 4vw, 3rem);
        }

        .taxonomy-media-badges {
          position: absolute;
          left: 18px;
          right: 18px;
          bottom: 18px;
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
        }

        .taxonomy-media-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 9px 12px;
          border-radius: 999px;
          background: rgba(18, 21, 18, 0.88);
          color: #f7f3ed;
          border: 1px solid rgba(255, 255, 255, 0.12);
          backdrop-filter: blur(6px);
          text-decoration: none;
        }

        .taxonomy-copy {
          display: grid;
          gap: 14px;
        }

        .taxonomy-kicker {
          margin: 0;
          font-size: 0.78rem;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          color: rgba(247, 243, 237, 0.76);
        }

        .taxonomy-title {
          margin: 0;
          font-family: Georgia, 'Times New Roman', serif;
          line-height: 1.02;
          font-size: clamp(2.2rem, 4vw, 4rem);
        }

        .taxonomy-summary {
          margin: 0;
          font-size: 1.02rem;
          line-height: 1.7;
          color: rgba(247, 243, 237, 0.88);
        }

        .taxonomy-actions {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
        }

        .taxonomy-link,
        .taxonomy-button {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 10px 14px;
          border-radius: 999px;
          text-decoration: none;
          border: 1px solid rgba(255, 255, 255, 0.16);
          color: #f7f3ed;
          background: rgba(247, 243, 237, 0.12);
        }

        .taxonomy-button {
          cursor: pointer;
          font: inherit;
        }

        .taxonomy-secondary {
          background: transparent;
        }

        .taxonomy-editorial {
          display: grid;
          gap: 18px;
          padding-top: 6px;
          border-top: 1px solid rgba(255, 255, 255, 0.12);
        }

        .taxonomy-editorial p,
        .taxonomy-editorial h3 {
          margin: 0;
        }

        .taxonomy-editorial p {
          line-height: 1.7;
          color: rgba(247, 243, 237, 0.88);
        }

        .taxonomy-section-list {
          display: grid;
          gap: 14px;
        }

        .taxonomy-section {
          display: grid;
          gap: 8px;
          padding: 18px;
          border-radius: 20px;
          background: rgba(247, 243, 237, 0.08);
          border: 1px solid rgba(255, 255, 255, 0.08);
        }

        .filters-bar {
          position: sticky;
          top: 0;
          z-index: 2;
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 12px;
          padding: 14px 16px;
          margin-bottom: 18px;
          border-radius: 22px;
          background: rgba(250, 247, 241, 0.96);
          border: 1px solid rgba(49, 70, 61, 0.12);
          box-shadow: 0 14px 30px rgba(31, 42, 35, 0.08);
          backdrop-filter: blur(10px);
        }

        .filters-label {
          margin: 0;
          color: #31463d;
          font-size: 0.78rem;
          letter-spacing: 0.12em;
          text-transform: uppercase;
        }

        .filters-list {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
          flex: 1;
        }

        .filter-chip {
          display: inline-flex;
          gap: 8px;
          align-items: center;
          padding: 8px 12px;
          border-radius: 999px;
          background: rgba(49, 70, 61, 0.08);
          color: #31463d;
        }

        .filter-chip strong {
          font-size: 0.72rem;
          letter-spacing: 0.08em;
          text-transform: uppercase;
        }

        .clear-filters {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 10px 14px;
          border-radius: 999px;
          text-decoration: none;
          background: #31463d;
          color: #f7f3ed;
          border: 1px solid #31463d;
          white-space: nowrap;
        }

        .load-more-wrap {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 12px;
          margin-top: 28px;
        }

        .pagination-error {
          margin: 0;
          padding: 14px 16px;
          border-radius: 18px;
          background: rgba(146, 62, 46, 0.08);
          border: 1px solid rgba(146, 62, 46, 0.18);
          color: #6f3024;
        }

        .load-more-button {
          padding: 12px 18px;
          border-radius: 999px;
          border: 1px solid #31463d;
          background: #31463d;
          color: #f7f3ed;
          cursor: pointer;
          text-transform: uppercase;
          letter-spacing: 0.12em;
          font-size: 0.73rem;
        }

        .load-more-button:disabled {
          cursor: wait;
          opacity: 0.72;
        }

        .page-footer {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          align-items: center;
          gap: 8px 14px;
          margin-top: 32px;
          padding: 18px 12px 6px;
          border-top: 1px solid rgba(49, 70, 61, 0.12);
          color: rgba(49, 70, 61, 0.72);
          font-size: 0.78rem;
          letter-spacing: 0.03em;
        }

        .footer-separator {
          color: rgba(49, 70, 61, 0.35);
        }

        .footer-link {
          color: #31463d;
          text-decoration: none;
        }

        .footer-link:hover,
        .footer-link:focus-visible {
          text-decoration: underline;
          text-underline-offset: 0.18em;
        }

        @media (max-width: 640px) {
          .initial-loading-grid {
            grid-template-columns: 1fr;
          }

          .taxonomy-landing {
            padding: 18px;
          }

          .taxonomy-landing-layout {
            grid-template-columns: 1fr;
          }

          .taxonomy-media {
            min-height: 260px;
          }

          .page-footer {
            flex-direction: column;
          }

          .footer-separator {
            display: none;
          }
        }
      </style>

      <main class="page-shell" data-state="${this.viewState}" data-mode="${this.explorationMode}">
        ${showActiveFilters ? this.renderActiveFiltersBar() : ''}
        ${this.renderContent()}
        ${showLoadMore ? this.renderLoadMoreButton() : ''}
        ${this.renderFooter()}
      </main>
    `;

    this.attachChildren();
  }

  private renderContent(): string {
    if (this.isInitialLoadingState()) {
      return this.renderInitialLoadingPlaceholder();
    }

    const shouldRenderHero = this.baseListingData !== null;
    const hasTaxonomyLanding = this.getVisibleTaxonomyLanding() !== null;

    return `
      ${this.renderTaxonomyLanding()}
      ${shouldRenderHero && !hasTaxonomyLanding ? '<mv-animalia-listing-hero></mv-animalia-listing-hero>' : ''}
      <mv-animalia-listing-section-head></mv-animalia-listing-section-head>
      ${this.renderBody()}
    `;
  }

  private renderTaxonomyLanding(): string {
    const taxonomyLanding = this.getVisibleTaxonomyLanding();

    if (!taxonomyLanding) {
      return '';
    }

    const mediaBadges = [
      this.renderTaxonomyLicenseBadge(taxonomyLanding),
      this.renderTaxonomyArtistBadge(taxonomyLanding),
      this.renderTaxonomyAttributionBadge(taxonomyLanding),
      taxonomyLanding.wikipediaUrl
        ? `<a class="taxonomy-media-badge" href="${this.escapeAttribute(taxonomyLanding.wikipediaUrl)}" target="_blank" rel="noreferrer">Wikipedia</a>`
        : '',
    ]
      .filter((badge) => badge !== '')
      .join('');
    const hasEditorialContent = Boolean(taxonomyLanding.description) || taxonomyLanding.sections.length > 0;

    return `
      <section class="taxonomy-landing" aria-label="Contexto taxonomico">
        <div class="taxonomy-landing-layout">
          <div class="taxonomy-media">
            ${taxonomyLanding.imageSrc
              ? `<img src="${this.escapeAttribute(taxonomyLanding.imageSrc)}" alt="${this.escapeAttribute(taxonomyLanding.imageAlt)}">`
              : '<div class="taxonomy-media-fallback" aria-hidden="true"><span>Animalia</span></div>'}
            ${mediaBadges ? `<div class="taxonomy-media-badges">${mediaBadges}</div>` : ''}
          </div>
          <div class="taxonomy-copy">
            <p class="taxonomy-kicker">${this.escapeHtml(taxonomyLanding.label)} en exploracion</p>
            <h1 class="taxonomy-title">${this.escapeHtml(taxonomyLanding.title)}</h1>
            ${taxonomyLanding.summary ? `<p class="taxonomy-summary">${this.escapeHtml(taxonomyLanding.summary)}</p>` : ''}
            <div class="taxonomy-actions">
              <a class="taxonomy-link taxonomy-secondary" href="/">Volver al listado general</a>
              ${hasEditorialContent
                ? `<button class="taxonomy-button" type="button" data-editorial-toggle>${this.isTaxonomyEditorialExpanded ? 'Ocultar contexto ampliado' : 'Mostrar contexto ampliado'}</button>`
                : ''}
            </div>
          </div>
        </div>
        ${this.isTaxonomyEditorialExpanded ? this.renderExpandedTaxonomyEditorial(taxonomyLanding) : ''}
      </section>
    `;
  }

  private renderExpandedTaxonomyEditorial(taxonomyLanding: AnimaliaListingTaxonomyLandingDTO): string {
    const sectionsMarkup = taxonomyLanding.sections
      .map(
        (section) => `
          <article class="taxonomy-section">
            <h3>${this.escapeHtml(section.title)}</h3>
            <p>${this.escapeHtml(section.body)}</p>
          </article>
        `,
      )
      .join('');

    return `
      <div class="taxonomy-editorial">
        ${taxonomyLanding.description ? `<p>${this.escapeHtml(taxonomyLanding.description)}</p>` : ''}
        ${sectionsMarkup ? `<div class="taxonomy-section-list">${sectionsMarkup}</div>` : ''}
      </div>
    `;
  }

  private renderBody(): string {
    if (this.shouldRenderStatus()) {
      return `<mv-animalia-listing-status data-status="${this.viewState}"></mv-animalia-listing-status>`;
    }

    return '<mv-animalia-listing-grid></mv-animalia-listing-grid>';
  }

  private shouldRenderStatus(): boolean {
    if (this.viewState === 'error' || this.viewState === 'empty') {
      return true;
    }

    if (this.viewState === 'loading') {
      return this.getVisibleItems().length === 0;
    }

    return this.getVisibleItems().length === 0;
  }

  private renderLoadMoreButton(): string {
    const isLoadingMore = this.viewState === 'loading' && this.explorationMode === 'listing' && Boolean(this.baseListingData);

    return `
      <div class="load-more-wrap">
        ${this.paginationErrorMessage ? `<p class="pagination-error">${this.escapeHtml(this.paginationErrorMessage)}</p>` : ''}
        <button class="load-more-button" type="button" data-load-more ${isLoadingMore ? 'disabled' : ''}>
          ${isLoadingMore ? 'Cargando mas fichas' : 'Cargar mas'}
        </button>
      </div>
    `;
  }

  private renderActiveFiltersBar(): string {
    const activeFilters = this.getVisibleActiveFilters();

    if (activeFilters.length === 0) {
      return '';
    }

    return `
      <section class="filters-bar" aria-label="Filtros activos">
        <p class="filters-label">Filtros activos</p>
        <div class="filters-list">
          ${activeFilters
            .map(
              (filter) => `
                <span class="filter-chip">
                  <strong>${this.escapeHtml(filter.label)}</strong>
                  <span>${this.escapeHtml(filter.value)}</span>
                </span>
              `,
            )
            .join('')}
        </div>
        <a class="clear-filters" href="/">Clear filters</a>
      </section>
    `;
  }

  private renderFooter(): string {
    if (this.isInitialLoadingState()) {
      return '';
    }

    return `
      <footer class="page-footer" aria-label="Informacion de contacto y autoria">
        <span>Desarrollado por <a class="footer-link" href="https://x.com/nandawtek" target="_blank" rel="noreferrer">@nandawtek</a></span>
        <span class="footer-separator" aria-hidden="true">/</span>
        <a class="footer-link" href="${this.getContactMailtoHref()}" aria-label="Escribir a contacto arroba bichitos punto life">
          ${this.getVisibleEmailLabel()}
        </a>
      </footer>
    `;
  }

  private attachChildren(): void {
    if (!this.shadowRoot) {
      return;
    }

    const status = this.shadowRoot.querySelector('mv-animalia-listing-status') as
      | MvAnimaliaListingStatus
      | null;
    const hero = this.shadowRoot.querySelector('mv-animalia-listing-hero') as MvAnimaliaListingHero | null;
    const sectionHead = this.shadowRoot.querySelector('mv-animalia-listing-section-head') as
      | MvAnimaliaListingSectionHead
      | null;
    const grid = this.shadowRoot.querySelector('mv-animalia-listing-grid') as MvAnimaliaListingGrid | null;

    sectionHead?.setState(this.getSectionHeadState());

    if (hero && this.baseListingData) {
      hero.setHero(this.baseListingData.hero);
    }

    if (status) {
      this.configureStatus(status);
    }

    if (grid) {
      grid.setItems(this.getVisibleItems());
      grid.setDetailQuery(this.getDetailQuery());
    }
  }

  private restoreSearchFocus(trigger: 'auto' | 'manual' | 'url'): void {
    if (trigger === 'url') {
      return;
    }

    const sectionHead = this.shadowRoot?.querySelector('mv-animalia-listing-section-head') as
      | MvAnimaliaListingSectionHead
      | null;

    sectionHead?.focusSearchInput();
  }

  private configureStatus(status: MvAnimaliaListingStatus): void {
    const hasFilters = this.getVisibleActiveFilters().length > 0;
    const currentSearchQuery = this.getCurrentSearchQuery();

    if (this.viewState === 'loading') {
      if (this.explorationMode === 'search' && currentSearchQuery !== null) {
        status.setStatus(
          'loading',
          'Buscando especies',
          `Estamos consultando coincidencias para "${currentSearchQuery}" dentro del catalogo actual.`,
          {
            kicker: 'Estado de la busqueda',
          },
        );
        return;
      }

      status.setStatus(
        'loading',
        'Preparando el listado editorial',
        'Estamos buscando especies publicables para componer la primera tanda de fichas.',
      );
      return;
    }

    if (this.viewState === 'empty') {
      if (this.explorationMode === 'search' && currentSearchQuery !== null) {
        status.setStatus(
          'empty',
          'No encontramos coincidencias',
          `No vimos resultados para "${currentSearchQuery}". Prueba otro termino o limpia la busqueda para volver al listado editorial.`,
          {
            kicker: 'Estado de la busqueda',
              secondaryAction: {
                href: '/',
                label: 'Volver al listado',
              },
            },
        );
        return;
      }

      status.setStatus(
        'empty',
        hasFilters ? 'No hay resultados para estos filtros' : 'No hay fichas disponibles',
        hasFilters
          ? 'Prueba a limpiar los filtros activos para volver al listado general de especies.'
          : 'Ahora mismo no hay especies publicables que encajen en el listado visible.',
        hasFilters
          ? {
              secondaryAction: {
                href: '/',
                label: 'Clear filters',
              },
            }
          : {},
      );
      return;
    }

    if (this.viewState === 'error') {
      if (this.explorationMode === 'search' && currentSearchQuery !== null) {
        status.setStatus(
          'error',
          'No pudimos completar la busqueda',
          this.errorMessage || 'Prueba de nuevo en unos instantes para volver a consultar esta especie.',
          {
            showRetry: true,
            kicker: 'Estado de la busqueda',
            secondaryAction: {
              href: '/',
              label: 'Volver al listado',
            },
          },
        );
        return;
      }

      status.setStatus(
        'error',
        'No pudimos recuperar las fichas',
        this.errorMessage || 'Prueba de nuevo en unos instantes para recomponer la home editorial.',
        {
          showRetry: true,
          secondaryAction: hasFilters
            ? {
                href: '/',
                label: 'Clear filters',
              }
            : null,
        },
      );
    }
  }

  private getSectionHeadState(): AnimaliaListingSectionHeadState {
    return {
      mode: this.explorationMode,
      draftQuery: this.draftQuery,
      appliedQuery: this.appliedQuery,
      pendingQuery: this.pendingQuery,
      resultCount: this.searchData?.resultCount ?? 0,
      isLoading: this.viewState === 'loading',
    };
  }

  private getVisibleItems() {
    if (this.explorationMode === 'search') {
      return this.searchData?.items ?? [];
    }

    return this.baseListingData?.items ?? [];
  }

  private getActiveFiltersFromLocation() {
    return resolveAnimaliaListingFiltersFromLocation(
      getCurrentLocation().path,
      getCurrentLocation().query,
    ).filters;
  }

  private getSearchQueryFromLocation(): string | null {
    const rawSearch = getCurrentLocation().query.get('search') ?? null;

    if (typeof rawSearch !== 'string') {
      return null;
    }

    const trimmedSearch = rawSearch.trim();

    return trimmedSearch !== '' ? trimmedSearch : null;
  }

  private getVisibleActiveFilters() {
    if (this.explorationMode === 'search') {
      return [];
    }

    return this.baseListingData?.activeFilters.length
      ? this.baseListingData.activeFilters
      : toAnimaliaNavigableFilters(this.getActiveFiltersFromLocation());
  }

  private getDetailQuery(): Readonly<Record<string, string>> {
    return {
      returnTo: this.getCurrentListingRoute(),
    };
  }

  private getCurrentSearchQuery(): string | null {
    if (this.pendingQuery !== '') {
      return this.pendingQuery;
    }

    if (this.appliedQuery.trim() !== '') {
      return this.appliedQuery.trim();
    }

    const draftQuery = this.draftQuery.trim();

    return draftQuery !== '' && this.explorationMode === 'search' ? draftQuery : null;
  }

  private isInitialLoadingState(): boolean {
    return this.viewState === 'loading' && this.baseListingData === null && this.searchData === null;
  }

  private renderInitialLoadingPlaceholder(): string {
    return `
      <section class="initial-loading" data-loading-placeholder="listing" aria-hidden="true">
        <div class="initial-loading-block"></div>
        <div class="initial-loading-block"></div>
        <div class="initial-loading-grid">
          <div class="initial-loading-card"></div>
          <div class="initial-loading-card"></div>
        </div>
      </section>
    `;
  }

  private shouldHideIndexableContent(): boolean {
    return this.isInitialLoadingState();
  }

  private getCrossedAutoThreshold(previousValue: string, nextValue: string): 5 | 10 | null {
    const previousLength = previousValue.trim().length;
    const nextLength = nextValue.trim().length;

    if (nextLength <= previousLength) {
      return null;
    }

    for (const threshold of AUTO_SEARCH_THRESHOLDS) {
      if (previousLength < threshold && nextLength >= threshold) {
        return threshold;
      }
    }

    return null;
  }

  private normalizeSearchLocation(query: string): void {
    this.replaceLocation(
      this.getListingBaseHref(true, query),
    );
  }

  private getCurrentListingRoute(): string {
    const location = getCurrentLocation();
    return buildRoute(location.path, Object.fromEntries(location.query.entries()));
  }

  private getListingBaseHref(includeSearch: boolean, searchOverride?: string): string {
    const location = getCurrentLocation();
    const resolvedFilters = resolveAnimaliaListingFiltersFromLocation(location.path, location.query);
    const query = Object.fromEntries(location.query.entries());

    if (resolvedFilters.primaryTaxonomy) {
      delete query[resolvedFilters.primaryTaxonomy.filterKey];
    }

    if (!includeSearch) {
      delete query.search;
    } else if (typeof searchOverride === 'string' && searchOverride.trim() !== '') {
      query.search = searchOverride;
    }

    const basePath = resolvedFilters.primaryTaxonomy
      ? buildAnimaliaTaxonomyCanonicalRoute(
          resolvedFilters.primaryTaxonomy.filterKey,
          resolvedFilters.primaryTaxonomy.value,
        )
      : '/';

    return buildRoute(basePath, query);
  }

  private renderTaxonomyLicenseBadge(taxonomyLanding: AnimaliaListingTaxonomyLandingDTO): string {
    if (!taxonomyLanding.imageLicenseName) {
      return '';
    }

    const label = `© ${this.escapeHtml(taxonomyLanding.imageLicenseName)}`;

    if (taxonomyLanding.imageLicenseUrl) {
      return `<a class="taxonomy-media-badge" href="${this.escapeAttribute(taxonomyLanding.imageLicenseUrl)}" target="_blank" rel="noreferrer">${label}</a>`;
    }

    return `<span class="taxonomy-media-badge">${label}</span>`;
  }

  private renderTaxonomyArtistBadge(taxonomyLanding: AnimaliaListingTaxonomyLandingDTO): string {
    if (!taxonomyLanding.imageArtist) {
      return '';
    }

    return `<span class="taxonomy-media-badge">✎ ${this.escapeHtml(taxonomyLanding.imageArtist)}</span>`;
  }

  private renderTaxonomyAttributionBadge(taxonomyLanding: AnimaliaListingTaxonomyLandingDTO): string {
    if (!taxonomyLanding.imageAttributionRequired) {
      return '';
    }

    const title = taxonomyLanding.imageCredit ?? taxonomyLanding.imageSource ?? 'Atribucion requerida';

    return `<span class="taxonomy-media-badge" title="${this.escapeAttribute(title)}">✓ atribucion</span>`;
  }

  private getVisibleTaxonomyLanding(): AnimaliaListingTaxonomyLandingDTO | null {
    const resolvedFilters = resolveAnimaliaListingFiltersFromLocation(
      getCurrentLocation().path,
      getCurrentLocation().query,
    );

    if (!resolvedFilters.primaryTaxonomy) {
      return null;
    }

    const mappedLanding = this.baseListingData?.taxonomyLanding ?? null;

    if (mappedLanding) {
      return mappedLanding;
    }

    return {
      key: resolvedFilters.primaryTaxonomy.filterKey,
      label: resolvedFilters.primaryTaxonomy.label,
      value: resolvedFilters.primaryTaxonomy.value,
      title: resolvedFilters.primaryTaxonomy.value,
      summary: null,
      description: null,
      imageSrc: null,
      imageAlt: resolvedFilters.primaryTaxonomy.value,
      imageLicenseName: null,
      imageLicenseUrl: null,
      imageArtist: null,
      imageCredit: null,
      imageAttributionRequired: false,
      imageSource: null,
      wikipediaUrl: null,
      sections: [],
      completed: false,
      canonicalUrl: buildAnimaliaTaxonomyCanonicalRoute(
        resolvedFilters.primaryTaxonomy.filterKey,
        resolvedFilters.primaryTaxonomy.value,
      ),
    };
  }

  private applySeo(): void {
    if (this.shouldHideIndexableContent()) {
      return;
    }

    const taxonomyLanding = this.getVisibleTaxonomyLanding();
    const currentSearchQuery = this.getCurrentSearchQuery();

    if (this.explorationMode === 'search' && currentSearchQuery !== null) {
      const resultCount = this.searchData?.resultCount ?? 0;
      const taxonomyLabel = taxonomyLanding ? `${taxonomyLanding.label.toLowerCase()} ${taxonomyLanding.value}` : 'archivo editorial';
      const searchPath = this.getCurrentListingRoute();

      applySeoMetadata({
        title: `Busqueda de ${currentSearchQuery}`,
        description: `Resultados de busqueda para ${currentSearchQuery} dentro del ${taxonomyLabel} de Bichitos.life. ${resultCount} coincidencias visibles en el archivo editorial de insectos.`,
        path: searchPath,
        canonicalUrl: buildSiteUrl(this.getListingBaseHref(false)),
        robots: 'noindex,follow',
        jsonLd: {
          '@context': 'https://schema.org',
          '@type': 'SearchResultsPage',
          name: `Busqueda de ${currentSearchQuery}`,
          url: buildSiteUrl(searchPath),
          isPartOf: createWebsiteJsonLd(),
          inLanguage: 'es',
        },
      });
      return;
    }

    if (taxonomyLanding) {
      const description = taxonomyLanding.description
        ?? taxonomyLanding.summary
        ?? `Explora ${taxonomyLanding.title} dentro de la taxonomia ${taxonomyLanding.label.toLowerCase()} de Bichitos.life.`;

      applySeoMetadata({
        title: `${taxonomyLanding.title} | ${taxonomyLanding.label}`,
        description,
        path: taxonomyLanding.canonicalUrl,
        canonicalUrl: buildSiteUrl(taxonomyLanding.canonicalUrl),
        imageUrl: taxonomyLanding.imageSrc,
        imageAlt: taxonomyLanding.imageAlt,
        jsonLd: [
          createWebsiteJsonLd(),
          {
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: taxonomyLanding.title,
            description,
            url: buildSiteUrl(taxonomyLanding.canonicalUrl),
            inLanguage: 'es',
            about: taxonomyLanding.title,
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: 'Bichitos.life',
                item: buildSiteUrl('/'),
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: taxonomyLanding.label,
                item: buildSiteUrl(`/${taxonomyLanding.key}/${encodeURIComponent(taxonomyLanding.value)}`),
              },
              {
                '@type': 'ListItem',
                position: 3,
                name: taxonomyLanding.title,
                item: buildSiteUrl(taxonomyLanding.canonicalUrl),
              },
            ],
          },
        ],
      });
      return;
    }

    const visibleItemsCount = this.getVisibleItems().length;
    const activeFilters = this.getVisibleActiveFilters();
    const description = activeFilters.length > 0
      ? `Explora ${visibleItemsCount} fichas visibles de insectos filtradas por ${activeFilters.map((filter) => `${filter.label.toLowerCase()} ${filter.value}`).join(', ')} en Bichitos.life.`
      : `Explora ${visibleItemsCount} fichas visibles de insectos con taxonomias navegables y contexto editorial en Bichitos.life.`;

    applySeoMetadata({
      title: 'Insectos y fichas editoriales',
      description,
      path: '/',
      canonicalUrl: buildSiteUrl('/'),
      jsonLd: [
        createWebsiteJsonLd(),
        {
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: 'Insectos y fichas editoriales',
          description,
          url: buildSiteUrl('/'),
          inLanguage: 'es',
        },
      ],
    });
  }

  private replaceLocation(route: string): void {
    window.history.replaceState(null, '', route);
  }

  private getContactMailtoHref(): string {
    const mailbox = ['con', 'tacto'].join('');
    const host = ['bichitos', 'life'].join('.');

    return `mailto:${mailbox}@${host}`;
  }

  private getVisibleEmailLabel(): string {
    const mailbox = ['con', 'tacto'].join('');
    const domainParts = ['bichitos', 'life'];

    return `${mailbox}@${domainParts[0]}.${domainParts[1]}`;
  }

  private escapeHtml(value: string): string {
    return value
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');
  }

  private escapeAttribute(value: string): string {
    return this.escapeHtml(value);
  }
}

if (!customElements.get('mv-animalia-home-page')) {
  customElements.define('mv-animalia-home-page', MvAnimaliaHomePage);
}
